import type { RoleAssignment, RoleDefinition } from "@azure/arm-authorization";
import type { VirtualMachine } from "@azure/arm-compute";
import type { ConfigOf } from "@p0security/shared/install/types";
import type { IntegrationConfig } from "@p0security/shared/integrations/shared";

import type { EntraIdComponents } from "./components.ts";

export namespace ms {
  export namespace models {
    // NB: There are a bazillion useless fields on these objects, like
    // "employeeHireDate" and "faxNumber"; those are ommitted from these types
    export type Group = {
      "@odata.type": string;
      displayName: string | null;
      mail: string | null;
      id: string;
      members: Group[] | User[] | null;
    };

    export type User = {
      "@odata.type": string;
      displayName: string | null;
      id: string;
      jobTitle: string | null;
      mail: string | null;
      userPrincipalName: string;
      userType?: string | null;
      signInActivity?: {
        lastSignInDateTime: string;
      };
      proxyAddresses: string[] | null;
      otherMails: string[] | null;
      identities: { issuer: string; issuerAssignedId: string }[] | null;
    };
    export type UserMfaDetails = {
      "@odata.type": string;
      id: string;
      userPrincipalName: string;
      isMfaCapable: boolean;
      isMfaRegistered?: boolean;
    };
    export type ServicePrincipal = {
      "@odata.type": "#microsoft.graph.servicePrincipal";
      id: string;
      displayName: string | undefined;
      appId: string | undefined;
    };

    export type UnifiedRoleDefinition = {
      "@odata.type": string;
      id: string;
      displayName: string | null;
      description: string | null;
      isBuiltIn: boolean | null;
      isEnabled: boolean | null;
    };
    export type UnifiedRoleAssignment = {
      "@odata.type": string;
      id: string;
      principalId: string;
      roleDefinitionId: string;
    };

    // Re-export Azure SDK types for consistency
    export type AzureRoleAssignment = RoleAssignment;
    export type AzureRoleDefinition = RoleDefinition;
    export type AzureVirtualMachine = VirtualMachine;
  }
}

export const EmailField = ["mail", "userPrincipalName", "otherMails"] as const;
export type EmailField = (typeof EmailField)[number];

// Legacy OAuth-based integration type
export type LegacyAzureAdIntegration = IntegrationConfig & {
  emailField: EmailField;
};

// New component-based integration type
export type EntraIdIntegration = ConfigOf<typeof EntraIdComponents>;

// Union type supporting both old and new integration methods
export type AzureAdIntegration = EntraIdIntegration | LegacyAzureAdIntegration;

// Helper functions for working with the union type
export const isLegacyAzureAdIntegration = (
  integration?: AzureAdIntegration
): integration is LegacyAzureAdIntegration => {
  return !!integration && "state" in integration;
};

export const isEntraIdIntegration = (
  integration?: AzureAdIntegration
): integration is EntraIdIntegration => {
  return !!integration && "iam-write" in integration;
};

export type SovereignCloudId =
  EntraIdIntegration["iam-write"][string]["sovereignCloudId"];

export const getAzureAdIntegrationState = (
  integration: AzureAdIntegration
): string | undefined => {
  if (isLegacyAzureAdIntegration(integration)) {
    return integration.state;
  }
  // For new integration, check if iam-write component has installed items
  return undefined; // Will be handled by isInstalled logic
};

export const DEFAULT_EMAIL_FIELD: EmailField = "userPrincipalName";

export const UUID_PATTERN =
  /^[0-9a-fA-F]{8}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{12}$/;
